import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { requestItemPhotoDownloadUrl } from '../../api/requests/index.js';

// WHY (#2140): read-only photo leaves resolve a photo objectKey to a signed
//   URL via the LIVE item/box endpoints by default. The archive snapshot view
//   must resolve through the snapshot-scoped endpoint (#2138) — live rows may
//   have been edited after the return, so the live key 404s. This context
//   injects that resolver; absent, the leaves keep their live behavior.
export type PhotoDownloadResolver = (objectKey: string) => Promise<string | null>;

const PhotoDownloadResolverContext = createContext<PhotoDownloadResolver | null>(null);

export function PhotoDownloadResolverProvider({
  resolver,
  children,
}: {
  resolver: PhotoDownloadResolver;
  children: ReactNode;
}) {
  return (
    <PhotoDownloadResolverContext.Provider value={resolver}>
      {children}
    </PhotoDownloadResolverContext.Provider>
  );
}

export function usePhotoDownloadResolver(): PhotoDownloadResolver | null {
  return useContext(PhotoDownloadResolverContext);
}

export function useRequestItemPhotoUrl(
  requestId: string,
  itemId: string,
  objectKey: string | null,
): string | null {
  const resolver = usePhotoDownloadResolver();
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setDownloadUrl(null);
    if (!objectKey || !isIssuedPhotoKey(objectKey)) return () => undefined;
    const download = resolver
      ? resolver(objectKey)
      : requestItemPhotoDownloadUrl(requestId, itemId, objectKey).then((r) => r.downloadUrl);
    void download
      .then((url) => {
        if (alive) setDownloadUrl(url);
      })
      .catch(() => {
        if (alive) setDownloadUrl(null);
      });
    return () => {
      alive = false;
    };
  }, [itemId, objectKey, requestId, resolver]);

  return downloadUrl;
}

function isIssuedPhotoKey(objectKey: string): boolean {
  return /^request-item-photo\/[0-9a-f-]{36}\/[0-9a-f-]{36}$/i.test(objectKey);
}
